import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Stack,
  Divider,
  Tooltip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import { useNavigate } from "react-router-dom";
import orderService from "../../repository/orderRepository";

const OrderHistory = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  const colors = {
    background: isDark ? "#11100F" : "#F6F3EF",
    card: isDark ? "#191715" : "#FFFFFF",
    input: isDark ? "#151412" : "#FCFBF9",
    text: isDark ? "#F4F0EA" : "#27231F",
    secondary: isDark ? "#AAA49C" : "#77716A",
    accent: isDark ? "#D1B99A" : "#806B52",
    accentSoft: isDark ? "rgba(209,185,154,.10)" : "rgba(128,107,82,.08)",
    border: isDark ? "rgba(255,255,255,.08)" : "rgba(50,40,30,.09)",
    success: isDark ? "#8FC6A3" : "#3D8054",
    warning: isDark ? "#D7B77A" : "#A66E18",
    danger: isDark ? "#D99494" : "#B54545",
  };

  const cardSx = {
    borderRadius: "14px",
    backgroundColor: colors.card,
    border: `1px solid ${colors.border}`,
    boxShadow: isDark
      ? "0 8px 30px rgba(0,0,0,.12)"
      : "0 8px 30px rgba(50,40,30,.045)",
  };

  const fetchOrders = async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);
      else setLoading(true);

      const response = await orderService.getAllOrders();

      const bookings = response.data?.bookings || [];

      const fetchedOrders = bookings.map((item) => {
        const booking = item?.bookingData || {};
        const product = booking?.productData?.product || {};
        const user = item?.userId || {};
        const address = booking?.deliveryAddress || {};

        const rawStatus = booking?.bookingStatus || "Pending";
        const status = rawStatus.replace(/^Order\s+/i, "");

        const rentAmount = Number(product?.rentAmount) || 0;
        const securityAmount = product?.isSecurity
          ? Number(product?.securityAmount) || 0
          : 0;

        return {
          id: booking?.bookingId || booking?._id || "N/A",
          date: booking?.bookingDate || "-",
          userName: user?.name || "Unknown",
          email: user?.email || "",
          address: [
            address?.street1,
            address?.city,
            address?.state,
            address?.country,
            address?.pinCode,
          ]
            .filter(Boolean)
            .join(", "),
          status,
          amount: rentAmount + securityAmount,
        };
      });

      setOrders(fetchedOrders);
    } catch (err) {
      console.error("Order history error:", err);
      setError(err?.response?.data?.message || "Failed to fetch order data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const filteredOrders = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return orders.filter((order) => {
      const matchesSearch =
        !query ||
        String(order.id).toLowerCase().includes(query) ||
        String(order.date).toLowerCase().includes(query) ||
        String(order.userName).toLowerCase().includes(query) ||
        String(order.email).toLowerCase().includes(query) ||
        String(order.address).toLowerCase().includes(query);

      const matchesStatus =
        filterStatus === "all" ||
        order.status.toLowerCase() === filterStatus.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  }, [orders, searchTerm, filterStatus]);

  const stats = useMemo(() => {
    const pending = orders.filter(
      (order) => order.status.toLowerCase() === "pending",
    ).length;

    const completed = orders.filter(
      (order) => order.status.toLowerCase() === "completed",
    ).length;

    const canceled = orders.filter(
      (order) => order.status.toLowerCase() === "canceled",
    ).length;

    const revenue = orders
      .filter((order) => order.status.toLowerCase() === "completed")
      .reduce((sum, order) => sum + Number(order.amount || 0), 0);

    return {
      total: orders.length,
      pending,
      completed,
      canceled,
      revenue,
    };
  }, [orders]);

  const getStatusConfig = (status) => {
    const normalized = String(status || "").toLowerCase();

    if (normalized === "completed") {
      return {
        color: colors.success,
        background: isDark ? "rgba(143,198,163,.10)" : "rgba(61,128,84,.09)",
        icon: <CheckCircleOutlineRoundedIcon sx={{ fontSize: 15 }} />,
      };
    }

    if (normalized === "canceled" || normalized === "cancelled") {
      return {
        color: colors.danger,
        background: isDark ? "rgba(217,148,148,.10)" : "rgba(181,69,69,.08)",
        icon: <CancelOutlinedIcon sx={{ fontSize: 15 }} />,
      };
    }

    return {
      color: colors.warning,
      background: isDark ? "rgba(215,183,122,.10)" : "rgba(166,110,24,.08)",
      icon: <PendingActionsOutlinedIcon sx={{ fontSize: 15 }} />,
    };
  };

  const formatAmount = (amount) =>
    `₹${Number(amount || 0).toLocaleString("en-IN")}`;

  const formatDate = (date) => {
    if (!date || date === "-") return "-";

    const parsed = new Date(date);

    if (Number.isNaN(parsed.getTime())) return date;

    return parsed.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const StatCard = ({ title, value, icon, accent = false }) => (
    <Paper
      elevation={0}
      sx={{
        ...cardSx,
        p: 2.3,
        minHeight: 112,
        display: "flex",
        alignItems: "center",
        gap: 1.8,
        backgroundColor: accent ? colors.accentSoft : colors.card,
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: accent
            ? isDark
              ? "rgba(209,185,154,.16)"
              : "rgba(128,107,82,.12)"
            : colors.accentSoft,
          color: colors.accent,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 11,
            color: colors.secondary,
            fontWeight: 500,
            mb: 0.5,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            fontSize: 24,
            fontWeight: 650,
            lineHeight: 1.1,
            color: colors.text,
          }}
        >
          {value}
        </Typography>
      </Box>
    </Paper>
  );

  const StatusChip = ({ status }) => {
    const config = getStatusConfig(status);

    return (
      <Chip
        icon={config.icon}
        label={status || "Pending"}
        size="small"
        sx={{
          height: 27,
          borderRadius: "7px",
          fontSize: 11,
          fontWeight: 650,
          color: config.color,
          backgroundColor: config.background,
          border: `1px solid ${config.color}22`,
          "& .MuiChip-icon": {
            color: config.color,
          },
        }}
      />
    );
  };

  const EmptyState = () => (
    <Box
      sx={{
        py: 9,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: 58,
          height: 58,
          borderRadius: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: colors.accentSoft,
          color: colors.accent,
          mb: 2,
        }}
      >
        <ReceiptLongOutlinedIcon sx={{ fontSize: 27 }} />
      </Box>

      <Typography
        sx={{
          fontSize: 16,
          fontWeight: 650,
          color: colors.text,
        }}
      >
        No orders found
      </Typography>

      <Typography
        sx={{
          fontSize: 12.5,
          color: colors.secondary,
          mt: 0.6,
        }}
      >
        Try changing your search or status filter.
      </Typography>
    </Box>
  );

  const MobileOrderCard = ({ order }) => (
    <Card
      elevation={0}
      sx={{
        ...cardSx,
        borderRadius: "12px",
      }}
    >
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1.5,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: colors.accent,
                wordBreak: "break-all",
              }}
            >
              #{order.id}
            </Typography>

            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 600,
                color: colors.text,
                mt: 0.7,
              }}
              noWrap
            >
              {order.userName}
            </Typography>

            <Typography
              sx={{
                fontSize: 11.5,
                color: colors.secondary,
                mt: 0.3,
              }}
            >
              {formatDate(order.date)}
            </Typography>
          </Box>

          <StatusChip status={order.status} />
        </Box>

        <Divider sx={{ my: 1.5, borderColor: colors.border }} />

        <Box sx={{ display: "grid", gap: 1 }}>
          <Box>
            <Typography
              sx={{ fontSize: 10.5, color: colors.secondary, mb: 0.25 }}
            >
              Email
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                color: colors.text,
                wordBreak: "break-word",
              }}
            >
              {order.email || "-"}
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{ fontSize: 10.5, color: colors.secondary, mb: 0.25 }}
            >
              Delivery Address
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                lineHeight: 1.5,
                color: colors.text,
              }}
            >
              {order.address || "-"}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 0.5,
            }}
          >
            <Typography sx={{ fontSize: 11, color: colors.secondary }}>
              Order Amount
            </Typography>

            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                color: colors.text,
              }}
            >
              {formatAmount(order.amount)}
            </Typography>
          </Box>
        </Box>

        <Button
          fullWidth
          variant="text"
          endIcon={
            <ArrowForwardIosRoundedIcon sx={{ fontSize: "12px !important" }} />
          }
          onClick={() => navigate(`/orderDetails/${order.id}`)}
          sx={{
            mt: 1.5,
            height: 38,
            borderRadius: "8px",
            color: colors.accent,
            fontSize: 12,
            fontWeight: 650,
            textTransform: "none",
            justifyContent: "space-between",
            px: 1.2,
          }}
        >
          View Order
        </Button>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "calc(100vh - 70px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: colors.background,
        }}
      >
        <CircularProgress size={30} sx={{ color: colors.accent }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100%",
        backgroundColor: colors.background,
        color: colors.text,
        p: { xs: 1.5, sm: 2.5, md: 4 },
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ maxWidth: 1500, mx: "auto" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: "11px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: colors.accentSoft,
                color: colors.accent,
              }}
            >
              <ReceiptLongOutlinedIcon sx={{ fontSize: 24 }} />
            </Box>

            <Box>
              <Typography
                sx={{
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: { xs: 23, sm: 28, md: 30 },
                  fontWeight: 600,
                  lineHeight: 1.1,
                  color: colors.text,
                }}
              >
                Order History
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: 11.5, sm: 13 },
                  color: colors.secondary,
                  mt: 0.5,
                }}
              >
                Manage bookings, customers and order activity
              </Typography>
            </Box>
          </Box>

          <Tooltip title="Refresh orders">
            <span>
              <IconButton
                onClick={() => fetchOrders(true)}
                disabled={refreshing}
                sx={{
                  width: 42,
                  height: 42,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "10px",
                  color: colors.text,
                  backgroundColor: colors.card,
                  "&:hover": {
                    backgroundColor: colors.accentSoft,
                  },
                }}
              >
                {refreshing ? (
                  <CircularProgress size={18} sx={{ color: colors.accent }} />
                ) : (
                  <RefreshRoundedIcon sx={{ fontSize: 20 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr 1fr",
              sm: "repeat(2,1fr)",
              md: "repeat(4,1fr)",
            },
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <StatCard
            title="Total Orders"
            value={stats.total}
            accent
            icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 21 }} />}
          />

          <StatCard
            title="Pending"
            value={stats.pending}
            icon={<PendingActionsOutlinedIcon sx={{ fontSize: 21 }} />}
          />

          <StatCard
            title="Completed"
            value={stats.completed}
            icon={<CheckCircleOutlineRoundedIcon sx={{ fontSize: 21 }} />}
          />

          <StatCard
            title="Completed Value"
            value={formatAmount(stats.revenue)}
            icon={<ShoppingBagOutlinedIcon sx={{ fontSize: 21 }} />}
          />
        </Box>

        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            p: { xs: 1.5, sm: 2 },
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Search order ID, customer, email or address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                ...{
                  "& .MuiOutlinedInput-root": {
                    height: 43,
                    borderRadius: "9px",
                    backgroundColor: colors.input,
                    color: colors.text,
                    "& fieldset": { borderColor: colors.border },
                    "&:hover fieldset": { borderColor: colors.accent },
                    "&.Mui-focused fieldset": { borderColor: colors.accent },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: colors.secondary,
                    opacity: 0.8,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon
                      sx={{
                        fontSize: 20,
                        color: colors.secondary,
                      }}
                    />
                  </InputAdornment>
                ),
              }}
            />

            <FormControl
              size="small"
              sx={{
                width: { xs: "100%", md: 180 },
                flexShrink: 0,
              }}
            >
              <InputLabel sx={{ color: colors.secondary }}>Status</InputLabel>

              <Select
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
                sx={{
                  height: 43,
                  borderRadius: "9px",
                  backgroundColor: colors.input,
                  color: colors.text,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.border,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.accent,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.accent,
                  },
                  "& .MuiSvgIcon-root": {
                    color: colors.secondary,
                  },
                }}
              >
                <MenuItem value="all">All Orders</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
                <MenuItem value="Canceled">Canceled</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 1.5,
            }}
          >
            <Typography
              sx={{
                fontSize: 11.5,
                color: colors.secondary,
              }}
            >
              Showing {filteredOrders.length} of {orders.length} orders
            </Typography>

            {(searchTerm || filterStatus !== "all") && (
              <Button
                onClick={() => {
                  setSearchTerm("");
                  setFilterStatus("all");
                }}
                sx={{
                  fontSize: 11,
                  minWidth: "auto",
                  color: colors.accent,
                  textTransform: "none",
                }}
              >
                Clear filters
              </Button>
            )}
          </Box>
        </Paper>

        {isMobile ? (
          <Stack spacing={1.5}>
            {filteredOrders.length === 0 ? (
              <Paper elevation={0} sx={cardSx}>
                <EmptyState />
              </Paper>
            ) : (
              filteredOrders.map((order) => (
                <MobileOrderCard key={order.id} order={order} />
              ))
            )}
          </Stack>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              ...cardSx,
              maxHeight: "calc(100vh - 390px)",
              minHeight: 300,
              overflow: "auto",
              "&::-webkit-scrollbar": {
                width: 7,
                height: 7,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: isDark ? "#3A3631" : "#D4CEC5",
                borderRadius: 10,
              },
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    "Order ID",
                    "Date",
                    "Customer",
                    "Address",
                    "Status",
                    "Amount",
                    "",
                  ].map((title, index) => (
                    <TableCell
                      key={title || index}
                      sx={{
                        backgroundColor: isDark ? "#1D1A18" : "#F3EFE9",
                        color: colors.secondary,
                        fontSize: 10.5,
                        fontWeight: 700,
                        letterSpacing: "1px",
                        textTransform: "uppercase",
                        borderBottom: `1px solid ${colors.border}`,
                        py: 1.8,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {title}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ borderBottom: 0 }}>
                      <EmptyState />
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOrders.map((order) => {
                    const status = getStatusConfig(order.status);

                    return (
                      <TableRow
                        key={order.id}
                        hover
                        onClick={() => navigate(`/orderDetails/${order.id}`)}
                        sx={{
                          cursor: "pointer",
                          "& td": {
                            borderBottom: `1px solid ${colors.border}`,
                          },
                          "&:hover": {
                            backgroundColor: colors.accentSoft,
                          },
                        }}
                      >
                        <TableCell sx={{ py: 1.8 }}>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: colors.accent,
                            }}
                          >
                            #{order.id}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ py: 1.8 }}>
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: colors.text,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatDate(order.date)}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ py: 1.8 }}>
                          <Typography
                            sx={{
                              fontSize: 12.5,
                              fontWeight: 600,
                              color: colors.text,
                            }}
                          >
                            {order.userName}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: 10.5,
                              color: colors.secondary,
                              mt: 0.25,
                            }}
                          >
                            {order.email || "No email"}
                          </Typography>
                        </TableCell>

                        <TableCell
                          sx={{
                            py: 1.8,
                            maxWidth: 280,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 11.5,
                              color: colors.secondary,
                              lineHeight: 1.5,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {order.address || "-"}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ py: 1.8 }}>
                          <Chip
                            icon={status.icon}
                            label={order.status || "Pending"}
                            size="small"
                            sx={{
                              height: 27,
                              borderRadius: "7px",
                              fontSize: 10.5,
                              fontWeight: 650,
                              color: status.color,
                              backgroundColor: status.background,
                              "& .MuiChip-icon": {
                                color: status.color,
                              },
                            }}
                          />
                        </TableCell>

                        <TableCell sx={{ py: 1.8 }}>
                          <Typography
                            sx={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: colors.text,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {formatAmount(order.amount)}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ py: 1.8 }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/orderDetails/${order.id}`);
                            }}
                            sx={{
                              width: 32,
                              height: 32,
                              color: colors.secondary,
                              border: `1px solid ${colors.border}`,
                              "&:hover": {
                                color: colors.accent,
                                backgroundColor: colors.accentSoft,
                              },
                            }}
                          >
                            <ArrowForwardIosRoundedIcon sx={{ fontSize: 13 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setError("")}
          severity="error"
          variant="filled"
          sx={{ borderRadius: "10px" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrderHistory;
